import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';

import type { BlockDefaultForm } from '@sites/entity-signal-form';
import {
    backgroundFilterVar,
    backgroundLayerVar,
    backgroundMarginVar,
    backgroundScrollEffectMode,
    backgroundVideoAtDevice,
    backgroundVar,
    blockHeightVar,
    blockWidthVar,
    borderSideVar,
    borderWidthVar,
    responsiveSpacingSideVar,
} from '@sites/entity-core';

import type { DeviceMode } from '@common/types/layout.type';
import type { DenormalizedDefaultBlockEntity, DenormalizedSectionEntity } from '@web/types';
import { environment } from '@web-env/environment';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebBlockFormService } from '@web/core/services/web-block-form.service';

import { BlockShapeDividers } from '../block-shape-dividers/block-shape-dividers.component';
import { DefaultSectionComponent } from '@web/layout/content/page-content/components/sections/default-section/default-section.component';
import { CSS_RESOLVERS } from '@web/core/constants/css-resolvers.const';

/** How close to the edge the block's own controls may sit inside it. */
const PADDING_TOP_THRESHOLD = 75;

/**
 * The box of an ordinary block and the rows inside it.
 *
 * Shared by `default-block` and `shared-block`, which differ only in the chrome
 * they draw around it — which is why this is a component of its own rather than
 * markup inlined into each. It is a *view*, not a wrapper: it renders the rows
 * itself instead of taking them through `<ng-content>`, so nothing about the
 * block's DOM is split across two files.
 */
@Component({
    selector: 'web-default-block-view',
    templateUrl: './default-block-view.component.html',
    styleUrls: ['./default-block-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BlockShapeDividers, DefaultSectionComponent, TranslateModule],
})
export class DefaultBlockViewComponent implements OnInit {
    public readonly entity = input.required<DenormalizedDefaultBlockEntity>();

    private readonly layoutFacade = inject(LayoutFacade);
    private readonly blockFormService = inject(WebBlockFormService);
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Styling is read from the signal form rather than the entity input, the way
     * `default-column` does it. Each settings group is its own signal, so editing
     * a padding no longer recomputes the background — which with a single bag it
     * did, for all forty-odd properties at once.
     */
    private form!: BlockDefaultForm;

    public readonly device = toSignal(this.layoutFacade.deviceMode$, { initialValue: 'desktop' as DeviceMode });

    public readonly sections = computed<DenormalizedSectionEntity[]>(() => this.entity().children);

    /**
     * The selected column, if any. The rows read it to know whether the selection
     * is inside them — it used to be one observable per row, threaded down from
     * the host; now the view that renders them resolves it once.
     */
    private readonly selectedEntity = toSignal(this.layoutFacade.selectedEntity$, { initialValue: null as never });

    public hasActiveColumn(section: DenormalizedSectionEntity): boolean {
        const selected = this.selectedEntity() as { entityType?: string; entityId?: string } | null;

        if (selected?.entityType !== 'column' || !selected?.entityId) {
            return false;
        }

        return section.children.some((column) => column.id === selected.entityId);
    }

    // One signal per settings group, each a single FieldTree node.
    private readonly paddingValue = computed(() => this.form.padding().value());
    private readonly borderValue = computed(() => this.form.border().value());
    private readonly backgroundValue = computed(() => this.form.background().value());

    // --- Gating classes -------------------------------------------------------

    // Each is its own signal and its own `[class.x]` binding, for the same reason
    // the custom properties are: toggling one must not re-diff the rest. The
    // editor binds only `has-own-height` — it never pins a block (a pinned one
    // has to stay in the flow so it can still be selected) and never hides one
    // (it shows a placeholder in its place instead).
    private readonly heightValue = computed(() => this.form.height().value());

    /** A height of its own is what makes the block centre its rows. */
    public readonly hasOwnHeight = computed<boolean>(() => Boolean(this.heightValue()?.desktop));

    /**
     * Which scroll effect the background layer is under. Parallax also swaps the
     * whole background DOM, so it is a template branch as well as a class.
     */
    private readonly scrollEffect = computed(() => backgroundScrollEffectMode(this.backgroundValue() as never));

    public readonly isParallax = computed<boolean>(() => this.scrollEffect() === 'parallax');
    public readonly isFixedBg = computed<boolean>(() => this.scrollEffect() === 'fixed');

    // --- One custom property per signal ---------------------------------------

    public readonly maxWidth = computed(() => blockWidthVar(this.form.width().value()));
    public readonly blockHeight = computed(() => blockHeightVar(this.heightValue()));
    public readonly blockHeightTablet = computed(() => blockHeightVar(this.heightValue(), 'tablet'));
    public readonly blockHeightMobile = computed(() => blockHeightVar(this.heightValue(), 'mobile'));

    public readonly paddingTop = computed(() => responsiveSpacingSideVar(this.paddingValue() as never, 'top'));
    public readonly paddingRight = computed(() => responsiveSpacingSideVar(this.paddingValue() as never, 'right'));
    public readonly paddingBottom = computed(() => responsiveSpacingSideVar(this.paddingValue() as never, 'bottom'));
    public readonly paddingLeft = computed(() => responsiveSpacingSideVar(this.paddingValue() as never, 'left'));
    public readonly borderTop = computed(() => borderSideVar(this.borderValue() as never, 'top'));
    public readonly borderRight = computed(() => borderSideVar(this.borderValue() as never, 'right'));
    public readonly borderBottom = computed(() => borderSideVar(this.borderValue() as never, 'bottom'));
    public readonly borderLeft = computed(() => borderSideVar(this.borderValue() as never, 'left'));
    public readonly borderTopWidth = computed(() => borderWidthVar(this.borderValue() as never, 'top'));
    public readonly borderRightWidth = computed(() => borderWidthVar(this.borderValue() as never, 'right'));
    public readonly borderBottomWidth = computed(() => borderWidthVar(this.borderValue() as never, 'bottom'));
    public readonly borderLeftWidth = computed(() => borderWidthVar(this.borderValue() as never, 'left'));
    public readonly bg = computed(() => backgroundVar(this.backgroundValue() as never, CSS_RESOLVERS));
    public readonly bgPosition = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'position'));
    public readonly bgRepeat = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'repeat'));
    public readonly bgSize = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'size'));
    public readonly bgFilter = computed(() => backgroundFilterVar(this.backgroundValue() as never));
    public readonly bgMargin = computed(() => backgroundMarginVar(this.backgroundValue() as never));

    public readonly paddingTopTablet = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'top', 'tablet')
    );
    public readonly paddingRightTablet = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'right', 'tablet')
    );
    public readonly paddingBottomTablet = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'bottom', 'tablet')
    );
    public readonly paddingLeftTablet = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'left', 'tablet')
    );
    public readonly borderTopTablet = computed(() => borderSideVar(this.borderValue() as never, 'top', 'tablet'));
    public readonly borderRightTablet = computed(() => borderSideVar(this.borderValue() as never, 'right', 'tablet'));
    public readonly borderBottomTablet = computed(() => borderSideVar(this.borderValue() as never, 'bottom', 'tablet'));
    public readonly borderLeftTablet = computed(() => borderSideVar(this.borderValue() as never, 'left', 'tablet'));
    public readonly borderTopWidthTablet = computed(() => borderWidthVar(this.borderValue() as never, 'top', 'tablet'));
    public readonly borderRightWidthTablet = computed(() =>
        borderWidthVar(this.borderValue() as never, 'right', 'tablet')
    );
    public readonly borderBottomWidthTablet = computed(() =>
        borderWidthVar(this.borderValue() as never, 'bottom', 'tablet')
    );
    public readonly borderLeftWidthTablet = computed(() =>
        borderWidthVar(this.borderValue() as never, 'left', 'tablet')
    );
    public readonly bgTablet = computed(() => backgroundVar(this.backgroundValue() as never, CSS_RESOLVERS, 'tablet'));
    public readonly bgPositionTablet = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'position', 'tablet')
    );
    public readonly bgRepeatTablet = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'repeat', 'tablet')
    );
    public readonly bgSizeTablet = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'size', 'tablet')
    );
    public readonly bgFilterTablet = computed(() => backgroundFilterVar(this.backgroundValue() as never, 'tablet'));
    public readonly bgMarginTablet = computed(() => backgroundMarginVar(this.backgroundValue() as never, 'tablet'));

    public readonly paddingTopMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'top', 'mobile')
    );
    public readonly paddingRightMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'right', 'mobile')
    );
    public readonly paddingBottomMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'bottom', 'mobile')
    );
    public readonly paddingLeftMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'left', 'mobile')
    );
    public readonly borderTopMobile = computed(() => borderSideVar(this.borderValue() as never, 'top', 'mobile'));
    public readonly borderRightMobile = computed(() => borderSideVar(this.borderValue() as never, 'right', 'mobile'));
    public readonly borderBottomMobile = computed(() => borderSideVar(this.borderValue() as never, 'bottom', 'mobile'));
    public readonly borderLeftMobile = computed(() => borderSideVar(this.borderValue() as never, 'left', 'mobile'));
    public readonly borderTopWidthMobile = computed(() => borderWidthVar(this.borderValue() as never, 'top', 'mobile'));
    public readonly borderRightWidthMobile = computed(() =>
        borderWidthVar(this.borderValue() as never, 'right', 'mobile')
    );
    public readonly borderBottomWidthMobile = computed(() =>
        borderWidthVar(this.borderValue() as never, 'bottom', 'mobile')
    );
    public readonly borderLeftWidthMobile = computed(() =>
        borderWidthVar(this.borderValue() as never, 'left', 'mobile')
    );
    public readonly bgMobile = computed(() => backgroundVar(this.backgroundValue() as never, CSS_RESOLVERS, 'mobile'));
    public readonly bgPositionMobile = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'position', 'mobile')
    );
    public readonly bgRepeatMobile = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'repeat', 'mobile')
    );
    public readonly bgSizeMobile = computed(() =>
        backgroundLayerVar(this.backgroundValue() as never, 'size', 'mobile')
    );
    public readonly bgFilterMobile = computed(() => backgroundFilterVar(this.backgroundValue() as never, 'mobile'));
    public readonly bgMarginMobile = computed(() => backgroundMarginVar(this.backgroundValue() as never, 'mobile'));

    // --- Content --------------------------------------------------------------

    /**
     * Both `<video>` elements are rendered when a file is picked; which one shows
     * is a global `.desktop-video` / `.mobile-video` rule. A mobile file stored as
     * `null` is "no video there", not "desktop's" — see `backgroundVideoAtDevice`.
     */
    public readonly desktopVideoUrl = computed(() => this.videoUrl('desktop'));
    public readonly mobileVideoUrl = computed(() => this.videoUrl('mobile'));

    private videoUrl(device: 'desktop' | 'mobile'): string | null {
        const file = backgroundVideoAtDevice(this.backgroundValue() as never, device);

        return file ? `${environment.fileManagerCdnUrl}${file.url}` : null;
    }

    /**
     * The editor keeps a block hidden on the previewed breakpoint on screen and
     * shows a placeholder in its place, rather than dropping it as the published
     * page does — otherwise it could not be selected again.
     */
    public readonly isHiddenOnView = computed<boolean>(() => {
        const hidden = this.form.hidden().value() as Record<string, boolean> | undefined;

        return Boolean(hidden?.[this.device()]);
    });

    public readonly hiddenHintText = computed<string>(() =>
        this.isHiddenOnView() ? `control_block_hidden_${this.device()}` : ''
    );

    public readonly isBlockSmallTopSpacing = computed<boolean>(
        () =>
            Number((this.paddingValue() as never as { desktop: { value: { top: number } } })?.desktop?.value?.top) <
            PADDING_TOP_THRESHOLD
    );

    public ngOnInit(): void {
        // Creating the form runs an `effect()` of its own, so it has to happen
        // outside any reactive context — a `computed` here throws NG0602.
        this.form = this.blockFormService.getOrCreateScoped(this.entity().id, this.destroyRef) as BlockDefaultForm;
    }
}
